import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { downloadFromBackend } from "../lib/download";
import BackdatedConfirmModal, { isBackdated } from "./BackdatedConfirmModal";

/**
 * MarginUploadModal — multi-step (idle → preview → committing → done) flow
 * for the margin-rules Excel upload feature.
 *
 * Used from BOTH:
 *   - /customers/{id}  (Customer detail → Margin rules card)
 *   - /margins         (global Margins page in the sidebar)
 *
 * The same backend endpoints (/margins/template.xlsx, /margins/upload,
 * /margins/upload-confirm) handle both call sites — the spreadsheet itself
 * carries a `Customer` column with all customers as a dropdown, so a single
 * upload can patch margins for any number of customers in one go.
 */
export default function MarginUploadModal({ state, setState, onCommitted, contextLabel, kind = "margin" }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Phase 3E-9Q — backdated bulk-commit confirmation.
  const [backdated, setBackdated] = useState(null);  // { open, effectiveDate, customerCount, ruleCount, retry, busy }

  // Endpoint switcher — the regular margin and DEF flows share the modal +
  // commit endpoint, only the template/upload endpoints differ.
  const isDef = kind === "def";
  const TEMPLATE_URL = isDef ? "/margins/def-template.xlsx" : "/margins/template.xlsx";
  const UPLOAD_URL = isDef ? "/margins/def-upload" : "/margins/upload";
  const TITLE = isDef ? "Upload DEF rates (Excel)" : "Upload margin rules (Excel)";
  const IDLE_BLURB = isDef
    ? "Upload DEF pricing rules for any number of customers in a single .xlsx file. Phase 3E-9P.2 — DEF now supports BOTH additive margin ('cpl', customer pays rack + margin) AND fixed rates ('dollar_per_litre', customer pays this $/L flat). Pick per row via the 'Margin Unit' column. Same customer/scope precedence engine as diesel."
    : "Upload margins for any number of customers in a single .xlsx file. The template includes dropdowns for customer, scope, network, product, station, city and province — only Margin and Effective Date are typed in. Rows that exactly match an existing rule are skipped automatically.";

  const close = () => { if (!uploading) setState(null); };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload a .xlsx file from the template");
      return;
    }
    setUploading(true);
    setState({ stage: "parsing", filename: file.name });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(UPLOAD_URL, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState({ stage: "preview", filename: file.name, preview: data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not parse the workbook");
      setState({ stage: "idle" });
    } finally { setUploading(false); }
  };

  const commit = async () => {
    // Phase 3E-9Q — detect the earliest backdated row so the modal can
    // summarise the impact BEFORE hitting the backend.
    const rows = state.preview?.rows || [];
    const backdatedRows = rows.filter((r) => isBackdated(r.effective_date));
    const earliest = backdatedRows.length
      ? backdatedRows.reduce((m, r) => (r.effective_date < m ? r.effective_date : m), backdatedRows[0].effective_date)
      : null;
    const uniqueCust = new Set(backdatedRows.map((r) => r.customer_id)).size;

    const doCommit = async (extraQuery = "") => {
      const { data } = await api.post(`/margins/upload-confirm${extraQuery}`, { rows });
      toast.success(
        `Inserted ${data.inserted} · Skipped ${data.skipped}` +
        (data.failed ? ` · ${data.failed} failed` : "")
      );
      setState({ stage: "done", result: data });
      setTimeout(onCommitted, 1200);
    };

    setUploading(true);
    setState({ ...state, stage: "committing" });
    try {
      if (earliest) {
        // Preflight modal — backend still guards on retry.
        setUploading(false);
        setState({ ...state, stage: "preview" });
        setBackdated({
          open: true,
          effectiveDate: earliest,
          contextLabel: `Commit ${rows.length} margin row${rows.length === 1 ? "" : "s"} — ${backdatedRows.length} backdated`,
          customerCount: uniqueCust,
          ruleCount: backdatedRows.length,
          retry: async () => {
            setBackdated((b) => b && { ...b, busy: true });
            setUploading(true);
            setState({ ...state, stage: "committing" });
            try {
              await doCommit("?confirm_backdated=true");
              setBackdated(null);
            } catch (e) {
              toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Commit failed");
              setBackdated((b) => b && { ...b, busy: false });
              setState({ ...state, stage: "preview" });
            } finally { setUploading(false); }
          },
        });
        return;
      }
      await doCommit();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "BACKDATED_MARGIN_CONFIRMATION_REQUIRED") {
        // Defensive backstop — backend flagged it, show modal.
        setBackdated({
          open: true,
          effectiveDate: d.effective_date,
          contextLabel: `Commit ${rows.length} margin row${rows.length === 1 ? "" : "s"}`,
          customerCount: uniqueCust,
          ruleCount: backdatedRows.length || 1,
          retry: async () => {
            setBackdated((b) => b && { ...b, busy: true });
            setUploading(true);
            try {
              await doCommit("?confirm_backdated=true");
              setBackdated(null);
            } catch (err) {
              toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Commit failed");
              setBackdated((b) => b && { ...b, busy: false });
            } finally { setUploading(false); }
          },
        });
        setState({ ...state, stage: "preview" });
        return;
      }
      toast.error(d?.message || d || "Commit failed");
      setState({ ...state, stage: "preview" });
    } finally { setUploading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const filename = await downloadFromBackend(TEMPLATE_URL, {});
      toast.success(`Template downloaded · ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={close}>
      <div
        className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="margin-upload-modal"
      >
        <div className="gf-card-header">
          <h3>{TITLE}</h3>
          <span className="text-xs text-zinc-500 font-mono">CONTEXT · {contextLabel || "ALL CUSTOMERS"}</span>
        </div>

        <div className="overflow-auto flex-1 p-5 space-y-4">
          {state.stage === "idle" && (
            <>
              <div className="text-sm text-zinc-600">
                {IDLE_BLURB}
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <button className="gf-btn gf-btn-outline" onClick={downloadTemplate} data-testid="upload-modal-download-template">
                  Download fresh template
                </button>
                <button className="gf-btn gf-btn-primary" onClick={() => fileInputRef.current?.click()} data-testid="upload-modal-pick-file">
                  Choose .xlsx to upload
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="margin-upload-input"
                />
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                Tip: the template is regenerated from live data every time you
                download — so newly added customers / stations always show up.
              </div>
            </>
          )}

          {state.stage === "parsing" && (
            <div className="text-center py-12 font-mono text-sm text-zinc-500">
              PARSING {state.filename}…
            </div>
          )}

          {(state.stage === "preview" || state.stage === "committing") && state.preview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryTile label="VALID" value={state.preview.row_count - state.preview.duplicate_count - state.preview.warning_count} tone="ok" />
                <SummaryTile label="WARNINGS" value={state.preview.warning_count} tone="warn" />
                <SummaryTile label="DUPLICATES" value={state.preview.duplicate_count} tone="muted" />
                <SummaryTile label="ERRORS" value={state.preview.error_count} tone="err" />
              </div>

              {state.preview.errors?.length > 0 && (
                <div className="border border-rose-200 bg-rose-50 p-3 text-xs font-mono">
                  <div className="font-bold tracking-widest text-rose-700 mb-1">ROWS NEEDING FIXES — NOT IMPORTED</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {state.preview.errors.map((e, i) => (
                      <li key={i}>Row {e.row_index}: {e.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="overflow-auto border border-zinc-200" style={{ maxHeight: 380 }}>
                <table className="gf-table">
                  <thead style={{ position: "sticky", top: 0, background: "white" }}>
                    <tr>
                      <th>ROW</th>
                      <th>STATUS</th>
                      <th>CUSTOMER</th>
                      <th>SCOPE</th>
                      <th>NET</th>
                      <th>PRODUCT</th>
                      <th>STATION / CITY / PROV</th>
                      <th className="text-right">MARGIN</th>
                      <th>EFFECTIVE</th>
                      <th>NOTES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(state.preview.rows || []).map((r, i) => (
                      <tr key={i} className={
                        r._status === "duplicate" ? "bg-zinc-50 text-zinc-400" :
                        r._status === "warning" ? "bg-amber-50" : ""
                      } data-testid={`upload-row-${r._row_index}`}>
                        <td className="font-mono text-xs">{r._row_index}</td>
                        <td className="font-mono text-xs uppercase">{r._status}</td>
                        <td className="text-xs">{r.customer_label}</td>
                        <td className="font-mono text-xs">{r.scope}</td>
                        <td className="font-mono text-xs">{r.network}</td>
                        <td className="font-mono text-xs">{r.product_canonical}</td>
                        <td className="text-xs">
                          {r.scope === "station" ? r.station_id : r.scope === "city" ? r.city : r.scope === "province" ? r.province : "—"}
                        </td>
                        <td className="gf-num">{r.margin_input} {r.margin_unit_input === "dollar_per_litre" ? "$/L" : "¢"}</td>
                        <td className="font-mono text-xs">{r.effective_date}</td>
                        <td className="text-xs text-amber-700">
                          {(r._warnings || []).join(" · ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {state.stage === "done" && state.result && (
            <div className="text-center py-12 space-y-2">
              <div className="text-lg font-mono tracking-widest text-emerald-700">UPLOAD COMPLETE</div>
              <div className="text-sm text-zinc-600">
                Inserted <span className="font-bold">{state.result.inserted}</span> ·
                Skipped <span className="font-bold">{state.result.skipped}</span>
                {state.result.failed ? <> · Failed <span className="font-bold">{state.result.failed}</span></> : null}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-zinc-200 flex justify-end gap-2">
          {state.stage === "preview" && (
            <button
              className="gf-btn gf-btn-primary"
              onClick={commit}
              disabled={uploading || (state.preview.row_count - state.preview.duplicate_count) === 0}
              data-testid="margin-upload-commit"
            >
              Insert {state.preview.row_count - state.preview.duplicate_count} rules
            </button>
          )}
          <button className="gf-btn gf-btn-outline" onClick={close} disabled={uploading} data-testid="margin-upload-close">
            {state.stage === "done" ? "Close" : "Cancel"}
          </button>
        </div>
      </div>

      {/* Phase 3E-9Q — backdated bulk-commit guard. */}
      <BackdatedConfirmModal
        open={!!backdated?.open}
        onCancel={() => setBackdated(null)}
        onConfirm={() => backdated?.retry && backdated.retry()}
        effectiveDate={backdated?.effectiveDate}
        contextLabel={backdated?.contextLabel}
        customerCount={backdated?.customerCount}
        ruleCount={backdated?.ruleCount}
        confirmLabel="Apply Backdated Margins"
        busy={!!backdated?.busy}
      />
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const palette = {
    ok:    "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn:  "border-amber-200 bg-amber-50 text-amber-800",
    muted: "border-zinc-200 bg-zinc-50 text-zinc-600",
    err:   "border-rose-200 bg-rose-50 text-rose-800",
  }[tone] || "border-zinc-200 bg-white text-zinc-700";
  return (
    <div className={`border p-3 ${palette}`}>
      <div className="overline">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value || 0}</div>
    </div>
  );
}
